/**
 * QMP-FE-5 — STEP 6 "Review & Publish".
 *
 * LAYOUT AND COPY ARE THE MOCKUP'S (`mockup-qmp-v79-official.html`, `#w6`
 * and `renderReview`, lines 621-624 and 1305-1336); every visual value is read
 * from its CSS (`.grp`, `.sheet`, `.pp`, v79:241-245 and 285-287).
 *
 * THE SHEET IS WHAT THE REVISION WOULD FREEZE (`lib/qmpReview`): per section
 * the saved draft, else what the current revision froze. Packages with the
 * same rules share one group; the sheet is the first package's, and each
 * package's parties are listed under it.
 *
 * NOTHING HERE DECIDES. Whether this person may publish, and what still
 * blocks it, is the server's (`can_publish`, `publish_blockers`).
 */
import React from 'react';
import {
  HowThisWorks, LoadError, QButton, useTip,
} from './ItpRulesStep';
import { useAuth } from '../../context/AuthContext';
import { declareQmp } from '../../lib/api';
import { apiErrorMessage } from '../../pages/TestRegisterPage';
import {
  reviewGroups, sheetRows, partyLine, describeBlocker, namedCompanyCount, AUTHORITY_CODES,
  partyChange, approverLines,
} from '../../lib/qmpReview';
import { formatDuration } from '../../lib/qmpRules';
import { fmtShortDay } from '../../lib/utils';

const CARD = 'bg-[#0d1526] border border-[#16223a] rounded-[12px] px-[26px] py-[24px] min-w-0';

/** The revision a publication would create (the 201 says the real one). */
export const nextRevision = (qmp) => Number((qmp.current_revision && qmp.current_revision.revision) || 0) + 1;

/** `{participation_id: name}` of the project's companies and the packages' parties. */
export function companyNames(editor) {
  const names = {};
  (editor.companies || []).forEach((c) => { names[c.id] = c.name || c.short_name || ''; });
  (editor.ctx.structure || []).forEach((r) => {
    [r.executing, r.verification, r.acceptance].forEach((p) => {
      if (p && p.participation_id && !names[p.participation_id]) names[p.participation_id] = p.name || '';
    });
  });
  return names;
}

// One part of a sheet value: `{t}` text, `{b}` bold, `{sep}` "·", `{dim}`.
// Exported for step 7, which prints the SAME sheet with the old rule struck
// through beside the new one (D14.10) — one renderer, never two.
export function Part({ p }) {
  if (p.sep) return <span className="text-[#5b6b85] mx-[7px]">·</span>;
  if (p.b != null) return <b className="text-[#f4f7fc] font-semibold">{p.b}</b>;
  if (p.dim != null) return <span className="text-[#5b6b85]">{p.dim}</span>;
  return <>{p.t}</>;
}

// v79 `ruleSheet`: a grid of label | value.
function Sheet({ rows, testId }) {
  return (
    <div
      className="grid grid-cols-[140px_minmax(0,1fr)] gap-x-[16px] gap-y-[7px] text-[12.5px] text-[#94a3b8] mt-[6px] mb-[12px]"
      data-testid={testId}
    >
      {rows.map((r) => (
        <React.Fragment key={r.label}>
          <div className="text-[#5b6b85] text-[10.5px] tracking-[.08em] uppercase pt-[2px]">{r.label}</div>
          <div data-testid={`${testId}-${r.label}`}>
            {r.lines.map((line, i) => (
              <React.Fragment key={i}>
                {i > 0 && <br />}
                {line.map((p, j) => <Part key={j} p={p} />)}
              </React.Fragment>
            ))}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

// v79 `.grp`: title, names when 1 < n < all, the first package's sheet, the parties.
// D32 (owner 136 A): a package whose parties changed since the revision in force
// shows the line that revision froze struck through above the live one, in
// orange — D14.10's one style of "changed" (step 7's `DiffSheet`).
function Group({ group, index, names, packages, children }) {
  return (
    <div
      className="border border-[#16223a] rounded-[9px] bg-[#0b1322] px-[16px] py-[14px] mb-[12px]"
      data-testid={`qp-group-${index}`}
    >
      <div className="text-[13px] font-bold mb-[4px]" data-testid={`qp-group-${index}-title`}>{group.title}</div>
      {group.names && (
        <div className="text-[12px] text-[#f97316] mb-[10px]" data-testid={`qp-group-${index}-names`}>{group.names}</div>
      )}
      <Sheet rows={sheetRows(group.rows[0], group.rules, names)} testId={`qp-sheet-${index}`} />
      <div
        className="text-[11.5px] text-[#5b6b85] mt-[8px] pt-[8px] border-t border-[#16223a]"
        data-testid={`qp-group-${index}-parties`}
      >
        {group.rows.map((row, i) => {
          const was = partyChange(row, packages && packages[row.qwp_id]);
          return (
            <React.Fragment key={row.qwp_id}>
              {i > 0 && <br />}
              {was && (
                <s className="line-through block" data-testid={`qp-parties-was-${row.qwp_id}`}>
                  {partyLine(was, group.rules, names)}
                </s>
              )}
              <span className={was ? 'text-[#f97316]' : undefined} data-testid={`qp-parties-${row.qwp_id}`}>
                {partyLine(row, group.rules, names)}
              </span>
            </React.Fragment>
          );
        })}
      </div>
      {children}
    </div>
  );
}

// Mockup `.err` (v79:330).
const ERR = 'mt-[10px] text-[12.5px] text-[#fca5a5]';

export const PUBLISH_FALLBACK = 'Could not publish the QMP';

/** Where a refusal of `POST /qmp/declare` is shown: `sigbox` (403, who may
 *  sign), `ack` (the "Before you sign" tick), `package` (a 422 naming a package:
 *  under its group), `publish` (anything else: beside the button). */
export function describePublishError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const message = apiErrorMessage(err, PUBLISH_FALLBACK);
  const code = detail && typeof detail === 'object' ? detail.code : null;
  if (status === 403) return { place: 'sigbox', message };
  if (status === 422 && code === 'GUIDE_ACKNOWLEDGEMENT_REQUIRED') return { place: 'ack', message };
  if (status === 422 && detail && typeof detail === 'object' && detail.qwp_id) {
    return { place: 'package', qwpId: detail.qwp_id, message };
  }
  return { place: 'publish', message };
}

/** "Sun 20 Sep 2026, 14:05" — the moment the server recorded; DATES-1: the
 *  app's one instant (`fmtShortDay`, 24 h), on TIMEZONE's clock. */
export function formatWhen(iso) {
  return fmtShortDay(iso, { time: true }) || '—';
}

// Mockup `.lnk` (v79:301-302).
const LNK = 'text-[12px] text-[#5b6b85] underline decoration-dotted hover:text-[#f97316] cursor-pointer';
// Mockup `.preview` / `.sigbox` title (v79:99, 169).
const BOX_T = 'text-[10.5px] tracking-[.1em] uppercase text-[#5b6b85] font-bold';

/** The server's `publish_blockers`, in the server's order, each said once and
 *  with the way to the step (and subarea) that resolves it. The screen does
 *  not work out a blocker of its own. */
function Blockers({ qmp, structure, onGo }) {
  const items = (qmp.publish_blockers || []).map((b) => describeBlocker(b, structure));
  if (!items.length) return null;
  return (
    <div
      className="bg-[#0b1322] border border-[#16223a] rounded-[9px] px-[16px] py-[14px] mt-[16px]"
      data-testid="qp-blockers"
    >
      <div className={`${BOX_T} mb-[8px]`}>Before this revision can be published</div>
      <ul>
        {items.map((it, i) => (
          <li
            key={`${it.code}-${it.qwpId || ''}-${i}`}
            className="list-none text-[12.5px] text-[#94a3b8] mb-[6px]"
            data-testid={`qp-blocker-${i}`}
            data-code={it.code}
          >
            {it.text}
            {it.places.map((pl) => (
              <button
                key={`${pl.step}-${pl.sub || ''}`}
                type="button"
                className={`ml-[10px] ${LNK}`}
                onClick={() => onGo(pl.step, pl.sub)}
                data-testid={`qp-blocker-${i}-go-${pl.step}${pl.sub ? `-${pl.sub}` : ''}`}
              >
                {`Step ${pl.step} · ${pl.title}`}
              </button>
            ))}
          </li>
        ))}
      </ul>
    </div>
  );
}

const TIPS6 = {
  aprdue: {
    title: 'Approval due',
    body: (
      <>
        How long each company has to Approve or Object after publication: 5 working days, set by
        ConstructQA for every revision and not configurable. ConstructQA sends reminders and records
        an overdue response against that company.{' '}
        <b>Time never approves the QMP automatically.</b>
      </>
    ),
  },
};

// Mockup `.chip` (v79:77-80, 148).
const CHIP = 'inline-flex items-center gap-[6px] text-[10px] font-semibold px-[8px] py-[3px] rounded-full whitespace-nowrap';
const CHIP_LOCK = `${CHIP} text-[#94a3b8] border border-[#1c2941] bg-[#0b1322]`;
const CHIP_OK = `${CHIP} text-[#86efac] border border-[rgba(34,197,94,.45)] bg-[rgba(34,197,94,.10)]`;
// Mockup `.sigbox` / `.ackbox` (v79:168-177).
const SIGBOX = 'bg-[#0b1322] border border-[#1c2941] rounded-[9px] px-[16px] py-[14px] mt-[16px]';
const SIGROW = 'flex flex-wrap gap-[12px] items-center mb-[9px] text-[12.5px]';
const SIGLBL = 'min-w-[120px] text-[#5b6b85] text-[12px]';

/** The governing company: the project's `project_owner_auto` participation. */
const governingCompany = (companies) => (companies || [])
  .find((c) => c && c.source === 'project_owner_auto') || null;

// "Who is publishing this revision" (v79:1317-1323).
function WhoIsPublishing({
  editor, qmp, signer, openTip, children,
}) {
  const { user } = useAuth() || {};
  const gov = governingCompany(editor.companies);
  return (
    <div className={SIGBOX} data-testid="qp-sigbox">
      <div className={`${BOX_T} mb-[10px]`}>Who is publishing this revision</div>
      <div className={SIGROW} data-testid="qp-prepared-by">
        <span className={SIGLBL}>Prepared by</span>
        <span className="flex flex-wrap items-center gap-[8px]">
          {gov ? gov.name : '—'}
          {gov && <span className={CHIP_LOCK}>Governing company</span>}
        </span>
      </div>
      <div className={SIGROW} data-testid="qp-published-by">
        <span className={SIGLBL}>Published by</span>
        <span className="flex flex-wrap items-center gap-[8px]">
          {signer ? ((user && (user.name || user.email)) || '—') : '—'}
          {signer && <span className={CHIP_OK}>Quality Management Plan Signatory</span>}
        </span>
      </div>
      <div className={SIGROW} data-testid="qp-approval-due">
        <span className={SIGLBL}>Approval due</span>
        <span>
          {formatDuration(qmp.approval_due) || '—'}<span className="text-[#5b6b85]">{' · set by ConstructQA'}</span>
          <QButton tipKey="aprdue" onOpen={openTip} />
        </span>
      </div>
      <div className="text-[12px] text-[#94a3b8] mt-[8px]">
        Publishing a QMP revision is a <b className="text-[#f4f7fc]">signing act</b>. Only a
        Quality Management Plan Signatory of the governing company can do it, and the person&apos;s name stays on
        the revision for its whole life.
      </div>
      {children}
    </div>
  );
}

// D32 (owner 132 A) — "Who will be asked to approve": per package, the companies
// publishing would ask and where each request goes, as the server forecasts it
// (`approvers_if_published`, D31) — before Revision 1 and after it. It reads;
// it decides nothing.
function WhoWillApprove({ structure, packages }) {
  const lines = approverLines(structure, packages);
  if (!lines.length) return null;
  return (
    <div
      className="bg-[#0b1322] border border-[#16223a] rounded-[9px] px-[16px] py-[14px] mt-[16px]"
      data-testid="qp-who-approves"
    >
      <div className={`${BOX_T} mb-[8px]`}>Who will be asked to approve</div>
      <ul>
        {lines.map((l) => (
          <li key={l.qwpId} className={LI} data-testid={`qp-who-approves-${l.qwpId}`}><b>{l.name}</b>: {l.text}</li>
        ))}
      </ul>
    </div>
  );
}

// "What happens when you publish" (v79:1324-1327).
const LI = 'list-none text-[12.5px] text-[#94a3b8] mb-[6px] [&_b]:text-[#f4f7fc]';
function WhatHappens({ n, count, due }) {
  return (
    <div
      className="bg-[#0b1322] border border-[#16223a] rounded-[9px] px-[16px] py-[14px] mt-[16px]"
      data-testid="qp-what-happens"
    >
      <div className={`${BOX_T} mb-[8px]`}>What happens when you publish</div>
      <ul>
        <li className={LI}>
          The rules above become <b>{`Revision ${n}`}</b>. The published revision records the final
          rules for each package, and the {count} companies named in the packages are asked to
          approve the rules for <b>their own packages</b> — that is the next step. They
          have <b>{due}</b> to approve or object.
        </li>
        <li className={LI}>
          Naming a contractor later does not change this revision — only that company is asked to approve.
        </li>
        <li className={LI}>
          Changing a rule creates a <b>new revision</b>, and only the companies in the changed packages
          are asked again. Signed records are never reopened.
        </li>
      </ul>
    </div>
  );
}

// "Before you sign" (v79:1328-1333) — D14.9, D26: the statement a signature
// confirms, beside the signature. THE WORDS AND THE VERSION ARE THE SERVER'S
// (`GET /qmp` `signing_statement`): the box draws what was served, and with
// nothing served there is no box and nothing to tick. EXPORTED because step 7
// signs too and must confirm the SAME statement: a second copy of this box
// would be a second thing to keep true. `hint` is the only difference between
// the two (`hint_publish` / `hint_approve`).
export function BeforeYouSign({
  statement, hint, ack, onAck, children,
}) {
  if (!statement) return null;
  return (
    <div className={`${SIGBOX} border-l-[3px] border-l-[#f97316]`} data-testid="qp-ackbox">
      <div className={`${BOX_T} mb-[8px]`}>Before you sign</div>
      <p className="mb-[10px] text-[12.5px] text-[#94a3b8]" data-testid="qp-ack-intro">{statement.intro}</p>
      <label className="flex gap-[9px] items-start text-[12.5px] text-[#f4f7fc]">
        <input
          type="checkbox"
          className="mt-[3px]"
          checked={ack}
          onChange={(e) => onAck(e.target.checked)}
          data-testid="qp-ack"
        />
        <span data-testid="qp-ack-tick">{statement.tick}</span>
      </label>
      <div className="text-[#94a3b8] text-[11.5px] mt-[8px]" data-testid="qp-ack-hint">{hint}</div>
      {children}
    </div>
  );
}

export default function PublishStep({
  editor, qmp, onGo, projectId,
}) {
  const n = nextRevision(qmp);
  const names = companyNames(editor);
  const groups = reviewGroups(editor.ctx.structure, editor.ctx.packages);
  const { open, pop } = useTip(TIPS6);
  const [ack, setAck] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [published, setPublished] = React.useState(null);
  // Whether this person may sign is the server's: an authority blocker says not.
  const signer = !(qmp.publish_blockers || []).some((b) => AUTHORITY_CODES.includes(b && b.code));
  const canPublish = Boolean(qmp.can_publish);

  const statement = qmp.signing_statement;
  // SIGN = `POST /qmp/declare` naming the version of the statement that was
  // served and ticked; then the plan is read again — never local optimism.
  // The revision shown is the server's answer.
  const publish = async () => {
    if (busy || !statement) return;
    setBusy(true);
    setError(null);
    try {
      const res = await declareQmp(projectId, { guide_acknowledged: true, guide_version: statement.version });
      setPublished((res && res.data) || null);
      setAck(false);
    } catch (err) {
      setError(describePublishError(err));
    }
    await qmp.reload();
    setBusy(false);
  };
  const errAt = (place) => (error && error.place === place ? (
    <div className={ERR} data-testid={`qp-error-${place}`}>{error.message}</div>
  ) : null);
  return (
    <div className={CARD} data-testid="qs-pane-6">
      <LoadError editor={editor} />
      <div className="text-[17px] font-bold flex items-center gap-[10px] mb-[8px] flex-wrap">Review &amp; Publish</div>
      <div className="text-[#94a3b8] text-[13px] mb-[16px] max-w-[760px]" data-testid="qp-help">
        {`Check the rules, then publish. Publishing creates Revision ${n} and freezes the rules.`}
      </div>
      <HowThisWorks testId="qp-how">
        Review the quality rules before publishing. Publishing creates <b>{`QMP Revision ${n}`}</b> and
        freezes these rules. Packages and company assignments are already active. After publication,
        each company is asked to approve the rules for the packages where it has a quality role.
      </HowThisWorks>
      <div data-testid="qp-review">
        {groups.map((g, i) => (
          <Group key={g.key} group={g} index={i} names={names} packages={editor.ctx.packages}>
            {error && error.place === 'package' && g.rows.some((r) => r.qwp_id === error.qwpId) && (
              <div className={ERR} data-testid="qp-error-package">{error.message}</div>
            )}
          </Group>
        ))}
        {error && error.place === 'package' && !groups.some((g) => g.rows.some((r) => r.qwp_id === error.qwpId)) && (
          <div className={ERR} data-testid="qp-error-package">{error.message}</div>
        )}
      </div>
      <Blockers qmp={qmp} structure={editor.ctx.structure} onGo={onGo} />
      <WhoIsPublishing editor={editor} qmp={qmp} signer={signer} openTip={open}>
        {errAt('sigbox')}
      </WhoIsPublishing>
      <WhoWillApprove structure={editor.ctx.structure} packages={editor.ctx.packages} />
      <WhatHappens
        n={n}
        count={namedCompanyCount(editor.ctx.structure)}
        due={formatDuration(qmp.approval_due) || '—'}
      />
      {/* A reader the server says cannot sign (another company, or no QMP
          Signatory) sees the sheet and the blockers, and no control to publish. */}
      {signer && (
        <>
          <BeforeYouSign statement={statement} hint={statement && statement.hint_publish} ack={ack} onAck={setAck}>
            {errAt('ack')}
          </BeforeYouSign>
          <div className="flex items-center mt-[16px]">
            <button
              type="button"
              disabled={!(canPublish && ack && statement) || busy}
              onClick={publish}
              className="px-[18px] py-[10px] rounded-[9px] font-bold text-[13.5px] bg-[#f97316] text-[#180b02] disabled:opacity-45 disabled:cursor-not-allowed"
              data-testid="qp-publish"
            >
              {`Publish QMP — Revision ${n}`}
            </button>
            {n === 1 && (
              <span className="ml-[14px] text-[11.5px] text-[#5b6b85]" data-testid="qp-publish-hint">
                Publishing creates the first controlled revision of the project QMP.
              </span>
            )}
          </div>
          {errAt('publish')}
        </>
      )}
      {published && (
        <div
          className="bg-[#0b1322] border border-[rgba(34,197,94,.45)] rounded-[9px] px-[16px] py-[14px] mt-[16px] text-[12.5px] text-[#94a3b8] [&_b]:text-[#f4f7fc]"
          data-testid="qp-published"
        >
          <b>{`Revision ${published.revision}`}</b>
          {` published by ${published.declared_by_name || '—'} on ${formatWhen(published.declared_at)}.`}
        </div>
      )}
      {pop}
    </div>
  );
}
